/**
 * Each person's overall total on People: what they owe in your groups (purchases) and their
 * tutoring (a family's lessons, or a tutor's pay credited to them), side by side and netted in
 * one total. Groups keep only their own items and real payments; tutoring is never put into a
 * group. Each line is shown in whole cents (see shown.ts) and every total is the exact sum of the
 * lines it covers, so no phantom penny can appear (Miles, 2026-10-23).
 */
import { shownCents } from "./shown.ts";
import type { Cents, Micro } from "./money.ts";

export type BalanceCategory = "purchases" | "tutoring";

export interface BalanceSource {
  category: BalanceCategory;
  label: string;
  /** Positive = they owe you; negative = you owe them. Exact, like the ledger. */
  balanceMicro: Micro;
}

export interface OverallTotal<T extends BalanceSource> {
  /** Each source as shown: whole cents, from the viewer's side. */
  lines: (T & { cents: Cents })[];
  /** The purchases lines added up (every group). */
  purchasesCents: Cents;
  /** The tutoring lines added up. */
  tutoringCents: Cents;
  /** purchases + tutoring: what they owe you overall (negative: you owe them). */
  totalCents: Cents;
}

/**
 * Lines and totals for one person. `flip` shows it from their own side (their own People page:
 * positive means they owe).
 */
export function overallTotal<T extends BalanceSource>(
  sources: readonly T[],
  flip = false,
): OverallTotal<T> {
  const sign = flip ? -1 : 1;
  const lines = sources.map((s) => ({ ...s, cents: sign * shownCents(s.balanceMicro) || 0 }));
  const sum = (cat: BalanceCategory) =>
    lines.filter((l) => l.category === cat).reduce((t, l) => t + l.cents, 0);
  const purchasesCents = sum("purchases");
  const tutoringCents = sum("tutoring");
  return { lines, purchasesCents, tutoringCents, totalCents: purchasesCents + tutoringCents };
}
